import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import {
  BmItemForm,
  BmMgmFeeGroupForm,
  BmMgmFeeVatPcntForm,
  BmMgmFeeVatValuesForm,
} from "../../../pojo/bm-form";
import { BmItem, BmMgmFeeVatPcnt, BmPeriodEnum } from "../../../pojo/bm-classes";
import { BmCalcType } from "../../../pojo/bm-enums";
import { BmCalcHelperService } from "../../general/bm-calc-helper.service";
import { BmDefaultFormCreatorService } from "../../general/bm-default-form-creator.service";
/**
 * SERVICE to manage rows VAT VALUES and VAT PCNT logic in Management Fees Group
 */
export class BmMgmVatFormService {
  /**
   * VAT pcnt
   * Creates a FormGroup and disable all items, which are not manual.
   */
  public static createMgmFeeVatPcntForm(vatPcnt: BmMgmFeeVatPcnt): FormGroup<BmMgmFeeVatPcntForm> {
    const vatPcntForm = new FormGroup<BmMgmFeeVatPcntForm>({
      isSelected: new FormControl(vatPcnt.isSelected ?? false),
      standardValue: new FormControl(vatPcnt.standardValue ?? 0, { validators: Validators.max(100) }),
      items: new FormArray(vatPcnt.items.map((data) => BmCalcHelperService.createBmItemForm(data))),
    });

    BmCalcHelperService.disableItems(vatPcntForm.controls.items);

    return vatPcntForm;
  }

  /**
   * VAT VALUES
   * Creates a FormGroup
   */
  public static createMgmFeeVatValuesForm(yearsNumber: number): FormGroup<BmMgmFeeVatValuesForm> {
    const items: BmItem[] = BmDefaultFormCreatorService.createItemsArray(yearsNumber);

    const vatValuesFa = new FormArray<FormGroup<BmItemForm>>(items.map((data) => BmCalcHelperService.createBmItemForm(data)));
    return new FormGroup<BmMgmFeeVatValuesForm>({
      items: vatValuesFa,
      totalRow: new FormControl<number>(0),
      totalRowPcnt: new FormControl<number>(0),
    });
  }

  /**
   * VAT PCNT - set standard value
   * Set standard value in each QUARTER, which is not MANUAL
   *
   * @param items - form array of vat pcnt items
   * @param newStdVal - new standard value
   */
  public static setNewStandardValue(items: FormArray<FormGroup<BmItemForm>>, newStdVal: number): void {
    const newStandardValue = Number(newStdVal);

    items.controls.forEach((itemFG) => {
      if (itemFG.value.calcType !== BmCalcType.MANUAL && itemFG.value.period.type === BmPeriodEnum.QUARTER) {
        BmCalcHelperService.patchAndDisable(itemFG, newStandardValue, BmCalcType.GENERATED);
      }
    });
  }

  /**
   * VAT PCNT - recalculate YEAR
   * Get corresponding year values of Mgm Fee and CatchUp. Get corresponding year VAT value
   * If catch up row is not selected => set 0 value
   * Formula: VAT value / Mgm Fee + CatchUp
   *
   * @param mgmFeeGroupFG - mgm fee form group data
   */
  public static recalculateVatYearPcnt(mgmFeeGroupFG: FormGroup<BmMgmFeeGroupForm>): void {
    const mgmFeeFA = mgmFeeGroupFG.controls.subGroups.controls.mgmFeeValues.controls.items;
    const catchUpFA = mgmFeeGroupFG.controls.subGroups.controls.catchUp.controls.items;
    const isCatchUpSelected = mgmFeeGroupFG.controls.subGroups.controls.catchUp.controls.isSelected.getRawValue();

    const vatValuesFA = mgmFeeGroupFG.controls.subGroups.controls.vatValues.controls.items;
    const vatPcntsFA = mgmFeeGroupFG.controls.subGroups.controls.vatPcnt.controls.items;

    const vatItemsByYear = BmCalcHelperService.groupItemsByYear(vatPcntsFA);

    vatItemsByYear.forEach(({ yearFG }) => {

      const mgmFee = Number(BmCalcHelperService.findItem(mgmFeeFA, yearFG.value.period.quarterNumber, yearFG.value.period.yearNumber).getRawValue().itemValue);

      let catchUp: number;
      if (isCatchUpSelected) {
        catchUp = Number(BmCalcHelperService.findItem(catchUpFA, yearFG.value.period.quarterNumber, yearFG.value.period.yearNumber).getRawValue().itemValue);
      } else {
        catchUp = 0;
      }

      const vatValue = Number(BmCalcHelperService.findItem(vatValuesFA, yearFG.value.period.quarterNumber, yearFG.value.period.yearNumber).getRawValue().itemValue);

      const newVatYearPcnt = (vatValue / (mgmFee + catchUp)) * 100;
      yearFG.controls.itemValue.patchValue(newVatYearPcnt, { emitEvent: false });
    });
  }

  /**
   * VAT VALUES - recalculate
   * Get corresponding period QUARTER of Mgm Fee and CatchUp. Get corresponding QUARTER VAT pcnt
   * If catch up row is not selected => set 0 value
   * Formula: (Mgm Fee + CatchUp) * vat pcnt
   *
   * @param mgmFeeGroupFG - mgm fee form group data
   */
  public static recalculateVatValues(mgmFeeGroupFG: FormGroup<BmMgmFeeGroupForm>): void {
    const mgmFeeFA = mgmFeeGroupFG.controls.subGroups.controls.mgmFeeValues.controls.items;
    const catchUpFA = mgmFeeGroupFG.controls.subGroups.controls.catchUp.controls.items;
    const isCatchUpSelected = mgmFeeGroupFG.controls.subGroups.controls.catchUp.controls.isSelected.getRawValue();

    const vatValuesFA = mgmFeeGroupFG.controls.subGroups.controls.vatValues.controls.items;
    const vatPcntsFA = mgmFeeGroupFG.controls.subGroups.controls.vatPcnt.controls.items;
    const totalVatRowValue = mgmFeeGroupFG.controls.subGroups.controls.vatValues.controls.totalRow;

    const vatItemsByYear = BmCalcHelperService.groupItemsByYear(vatValuesFA);

    vatItemsByYear.forEach(({ yearFG, quartersFG }) => {
      quartersFG.forEach((vatItemFG) => {
        const { quarterNumber, yearNumber } = vatItemFG.getRawValue().period;
        const mgmFeeVal = Number(BmCalcHelperService.findItem(mgmFeeFA, quarterNumber, yearNumber).getRawValue().itemValue);

        let catchUpVal: number;
        if (isCatchUpSelected) {
          catchUpVal = Number(BmCalcHelperService.findItem(catchUpFA, quarterNumber, yearNumber).getRawValue().itemValue);
        } else {
          catchUpVal = 0;
        }

        const vatPcntVal = Number(BmCalcHelperService.findItem(vatPcntsFA, quarterNumber, yearNumber).getRawValue().itemValue);
        const newQuarterVatValue = (mgmFeeVal + catchUpVal) * (vatPcntVal / 100);
        vatItemFG.controls.itemValue.patchValue(newQuarterVatValue, { emitEvent: false });
      });

      const yearVatSum = BmCalcHelperService.calculateSumOfQuarterValues(quartersFG); // YEAR vat value
      yearFG.controls.itemValue.patchValue(yearVatSum, { emitEvent: false });
    });

    totalVatRowValue.patchValue(BmCalcHelperService.recalculateRowTotal(vatValuesFA), { emitEvent: false });
  }
}
